/**
 * Загрузка документов в блок «Доходы» (решения от 25.09.2026): справки
 * 2-НДФЛ, сведения о трудовой деятельности, сведения о лицевом счёте
 * (СЗИ-ИЛС), справка из ЕГРИП, выписка ЕГИССО, справка СФР о пенсиях.
 * Можно выбрать несколько файлов сразу.
 *
 * Здесь нет строк с галочками: каждый документ отвечает на один вопрос
 * блока — работает ли, где, сколько получает, ИП ли, получает ли выплаты.
 * Ответ подставляется ЯВНЫМ действием у документа, потому что документы
 * отвечают не на тот же вопрос, что задаёт блок:
 *
 *   • 2-НДФЛ — сколько платил работодатель, но не работает ли должник
 *     там сейчас (код 2013 — компенсация за отпуск — признак увольнения);
 *   • трудовая — где работает сейчас: принят и не уволен;
 *   • СЗИ-ИЛС — сколько начислял работодатель по кварталам текущего года;
 *     выручает, когда 2-НДФЛ за текущий год нет или она пришла сканом;
 *   • ЕГИССО — какие выплаты были за период, а не получает ли сейчас;
 *   • справка СФР о пенсиях — только пенсии и ежемесячные выплаты по уходу;
 *     единовременные выплаты из ЕГИССО она не опровергает.
 */

import { useState } from 'react';
import type { Income } from '@bd/domain';
import { money } from '@bd/doc-engine/money';
import { api, type IncomeImport, type NdflSummary } from '../../../api.js';
import { DocumentUpload } from './DocumentUpload.js';

const MONTHS = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
const ruDate = (iso: string | null) => (iso ? iso.split('-').reverse().join('.') : '—');

type Of<K extends IncomeImport['kind']> = Extract<IncomeImport, { kind: K }>;

export function IncomeDocumentsImport({
  caseId,
  debtorInn,
  readOnly,
  onApply,
}: {
  caseId: string;
  debtorInn: string;
  readOnly: boolean;
  onApply: (patch: Partial<Income>) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [results, setResults] = useState<IncomeImport[]>([]);
  const [applied, setApplied] = useState<string[]>([]);

  const load = async (files: File[]) => {
    setBusy(true);
    setErrors([]);
    setResults([]);
    setApplied([]);
    const loaded: IncomeImport[] = [];
    const failed: string[] = [];
    for (const file of files) {
      try {
        loaded.push(await api.importIncomeDocument(caseId, file));
      } catch (e) {
        failed.push(`${file.name}: ${(e as Error).message}`);
      }
    }
    setResults(loaded);
    setErrors(failed);
    setBusy(false);
  };

  const apply = (patch: Partial<Income>, message: string) => {
    onApply(patch);
    setApplied((current) => [...current, message]);
  };

  const debtor = debtorInn.replace(/\s/g, '');
  const egissoHasPayments = results.some((r) => r.kind === 'egisso' && r.measures.length > 0);

  const action = (label: string, patch: Partial<Income>, message: string, disabled = false) => (
    <button type="button" className="link-button" disabled={readOnly || disabled} onClick={() => apply(patch, message)}>
      {label}
    </button>
  );

  const ndfl = (r: Of<'ndfl'>) => (
    <>
      {r.pagesWithoutText.length > 0 && (
        <p className="warning">
          {r.pagesWithoutText.length === 1 ? 'Страница' : 'Страницы'} {r.pagesWithoutText.join(', ')} без текста — похоже, скан.
          Справку с такой страницы разобрать нельзя, сверьте её вручную.
        </p>
      )}
      {r.certificates.length === 0 ? (
        <p className="hint">Справок с текстом в файле нет.</p>
      ) : (
        <table className="simple-table">
          <thead>
            <tr>
              <th>Год</th>
              <th>Налоговый агент</th>
              <th>Месяцы с зарплатой</th>
              <th>Средняя зарплата, ₽</th>
              <th>Всего дохода, ₽</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {r.certificates.map((c: NdflSummary, i) => (
              <tr key={i}>
                <td>{c.year ?? '—'}</td>
                <td>
                  {c.agentName}
                  {c.agentInn && <div className="hint">ИНН {c.agentInn}</div>}
                  {c.recipientInn && debtor && c.recipientInn !== debtor && <div className="error-text">Справка выдана на другой ИНН</div>}
                  {c.looksLikeDismissal && (
                    <div className="warning">Есть компенсация за неиспользованный отпуск (код 2013) — похоже на увольнение</div>
                  )}
                </td>
                <td>{c.salaryMonths.map((m) => MONTHS[m - 1]).join(', ') || '—'}</td>
                <td style={{ textAlign: 'right' }}>{money(c.averageSalary).digitsWithKopecks}</td>
                <td style={{ textAlign: 'right' }}>{c.total !== null ? money(c.total).digitsWithKopecks : '—'}</td>
                <td>
                  {action(
                    'Работает там сейчас — подставить',
                    {
                      employed: true,
                      employerName: c.agentName,
                      employerInn: c.agentInn ?? '',
                      monthlySalary: c.averageSalary,
                      salaryConfirmation: `Справка о доходах и суммах налога физического лица${c.year ? ` за ${c.year} год` : ''}`,
                    },
                    `Подставлено по справке 2-НДФЛ${c.year ? ` за ${c.year} год` : ''}: ${c.agentName}, ${money(c.averageSalary).digitsWithKopecks} ₽ в месяц.`,
                    c.averageSalary === 0,
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );

  const work = (r: Of<'work-history'>) => {
    const main = r.currentJobs.find((j) => !j.partTime) ?? r.currentJobs[0];
    return (
      <>
        {r.currentJobs.length === 0 ? (
          <>
            <p>
              Не трудоустроен
              {r.lastDismissal && `: последнее увольнение ${ruDate(r.lastDismissal.date)} — ${r.lastDismissal.employer}`}.
            </p>
            {action('Отметить: не трудоустроен', { employed: false }, 'Отмечено: должник не трудоустроен (по сведениям о трудовой деятельности).')}
          </>
        ) : (
          <>
            <ul>
              {r.currentJobs.map((job, i) => (
                <li key={i}>
                  {job.employer} — с {ruDate(job.since)}
                  {job.partTime ? ' (совместительство)' : ' (основное место)'}
                  {job.position && <div className="hint">{job.position}</div>}
                </li>
              ))}
            </ul>
            {main &&
              action(
                `Подставить: трудоустроен, ${main.employer}`,
                { employed: true, employerName: main.employer },
                `Подставлено по сведениям о трудовой деятельности: ${main.employer}. ИНН работодателя и зарплату возьмите из 2-НДФЛ.`,
              )}
          </>
        )}
      </>
    );
  };

  const egrip = (r: Of<'egrip'>) => (
    <>
      {r.inn && debtor && r.inn !== debtor && <p className="error-text">ИНН в справке не совпадает с ИНН должника в деле.</p>}
      {r.isEntrepreneur === null ? (
        <p className="warning">Из справки не понять, является ли должник предпринимателем. Сверьте вручную.</p>
      ) : r.isEntrepreneur ? (
        <>
          <p>Индивидуальный предприниматель, ОГРНИП {r.ogrnip}.</p>
          {action('Отметить: индивидуальный предприниматель', { isEntrepreneur: true, entrepreneurOgrnip: r.ogrnip ?? '' }, `Отмечено: ИП, ОГРНИП ${r.ogrnip}.`)}
        </>
      ) : (
        <>
          <p>Не является индивидуальным предпринимателем.</p>
          {action('Отметить: не является ИП', { isEntrepreneur: false, entrepreneurOgrnip: '' }, 'Отмечено: должник не является ИП (по справке из ЕГРИП).')}
        </>
      )}
    </>
  );

  const egisso = (r: Of<'egisso'>) =>
    r.measures.length === 0 ? (
      <>
        <p>Мер социальной поддержки за период нет.</p>
        {action('Отметить: выплат нет', { hasSocialPayments: false, socialPaymentsDescription: '' }, 'Отмечено: пособий и выплат нет (по выписке ЕГИССО).')}
      </>
    ) : (
      <>
        <table className="simple-table">
          <thead>
            <tr>
              <th>Мера поддержки</th>
              <th>Период</th>
              <th>Выплат</th>
              <th>Сумма, ₽</th>
            </tr>
          </thead>
          <tbody>
            {r.measures.map((m, i) => (
              <tr key={i}>
                <td>
                  {m.measure}
                  <div className="hint">
                    код {m.code || '—'}, {m.periodicity.toLowerCase()}
                  </div>
                </td>
                <td>
                  {ruDate(m.from)}–{ruDate(m.to)}
                </td>
                <td>{m.count}</td>
                <td style={{ textAlign: 'right' }}>{money(m.total).digitsWithKopecks}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="warning">
          Последняя выплата — {ruDate(r.lastPayment)}. Выписка показывает выплаты за период, а не то, получает ли должник их сейчас.
        </p>
        {action(
          'Подставить в «Пособия и выплаты»',
          { hasSocialPayments: true, socialPaymentsDescription: r.description },
          'Подставлено по выписке ЕГИССО в «Пособия и выплаты».',
        )}
      </>
    );

  const ils = (r: Of<'ils'>) => {
    // Свежие работодатели — за последние два года; прочие — для сведения
    const latest = r.employers[0]?.year ?? 0;
    const recent = r.employers.filter((e) => (e.year ?? 0) >= latest - 1);
    const older = r.employers.filter((e) => !recent.includes(e));
    return (
      <>
        {recent.length === 0 ? (
          <p className="hint">Выплат от работодателей в сведениях нет.</p>
        ) : (
          <table className="simple-table">
            <thead>
              <tr>
                <th>Работодатель</th>
                <th>Последний период</th>
                <th>Начислено, ₽</th>
                <th>В среднем в месяц, ₽</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {recent.map((e, i) => (
                <tr key={i}>
                  <td>
                    {e.employer}
                    <div className="hint">
                      за {e.year} год всего {money(e.yearTotal).digitsWithKopecks} ₽
                    </div>
                  </td>
                  <td>{e.lastPeriod.label}</td>
                  <td style={{ textAlign: 'right' }}>{money(e.lastPayments).digitsWithKopecks}</td>
                  <td style={{ textAlign: 'right' }}>{money(e.monthly).digitsWithKopecks}</td>
                  <td>
                    {action(
                      'Работает там сейчас — подставить',
                      {
                        employed: true,
                        employerName: e.employer,
                        monthlySalary: e.monthly,
                        salaryConfirmation: 'Сведения о состоянии индивидуального лицевого счёта застрахованного лица',
                      },
                      `Подставлено по сведениям о лицевом счёте: ${e.employer}, ${money(e.monthly).digitsWithKopecks} ₽ в месяц (${e.lastPeriod.label}).`,
                      e.monthly === 0,
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className="warning">
          Суммы начислены до вычета налога. Основное место и совместительство у одного работодателя сложены. Выплата при увольнении
          (компенсация отпуска) завышает среднюю за квартал увольнения.
        </p>
        {older.length > 0 && (
          <details>
            <summary className="hint">Прежние работодатели: {older.length}</summary>
            <ul className="hint">
              {older.map((e, i) => (
                <li key={i}>
                  {e.employer} — {e.lastPeriod.label}, {money(e.lastPayments).digitsWithKopecks} ₽
                </li>
              ))}
            </ul>
          </details>
        )}
      </>
    );
  };

  const sfr = (r: Of<'sfr-pension'>) =>
    r.isRecipient ? (
      <>
        <p>Справка содержит сведения о назначенных пенсиях или выплатах:</p>
        <p className="hint">{r.details || 'текст сведений не выделен — сверьте со справкой'}</p>
        {action(
          'Подставить в «Пособия и выплаты»',
          { hasSocialPayments: true, socialPaymentsDescription: r.details },
          'Подставлено по справке СФР в «Пособия и выплаты».',
          !r.details,
        )}
      </>
    ) : (
      <>
        <p>Не является получателем пенсии и ежемесячных выплат СФР.</p>
        {egissoHasPayments ? (
          <p className="warning">
            При этом выписка ЕГИССО показывает выплаты: справка СФР касается только пенсий и выплат по уходу, единовременные выплаты
            она не опровергает. Решите по выписке ЕГИССО.
          </p>
        ) : (
          action('Отметить: пенсий и выплат нет', { hasSocialPayments: false, socialPaymentsDescription: '' }, 'Отмечено: пенсий и выплат нет (по справке СФР).')
        )}
      </>
    );

  return (
    <div className="report-import">
      <DocumentUpload
        label="Загрузить документы о доходах"
        busyLabel="Читаю документы…"
        hint="2-НДФЛ, трудовая, лицевой счёт СФР, ЕГРИП, ЕГИССО, справка СФР. Можно несколько сразу. Файлы нигде не сохраняются."
        busy={busy}
        disabled={readOnly}
        onFiles={(files) => void load(files)}
      />

      {errors.map((error, i) => (
        <p key={i} className="error-text">
          {error}
        </p>
      ))}
      {applied.map((message, i) => (
        <p key={i} className="hint">
          {message}
        </p>
      ))}

      {results.length > 0 && (
        <div className="draft">
          {results.map((r, i) => (
            <div key={i} className="import-document">
              <p>
                <strong>{r.title}</strong>
              </p>
              {r.kind === 'ndfl' && ndfl(r)}
              {r.kind === 'work-history' && work(r)}
              {r.kind === 'egrip' && egrip(r)}
              {r.kind === 'egisso' && egisso(r)}
              {r.kind === 'sfr-pension' && sfr(r)}
              {r.kind === 'ils' && ils(r)}
            </div>
          ))}
          <div className="toolbar">
            <button type="button" className="link-button" onClick={() => setResults([])}>
              Закрыть
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
